import { useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDisparoCampaignMessages } from "@/hooks/useDisparos";
import {
  DISPARO_MESSAGE_STATUS_LABELS,
  type DisparoMessageStatus,
} from "@/lib/disparos-types";
import { formatPhoneForDisplay } from "@/lib/format-phone";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface DisparoCampaignMessagesProps {
  workspaceId: string;
  campaignId: string;
}

export function DisparoCampaignMessages({
  workspaceId,
  campaignId,
}: DisparoCampaignMessagesProps) {
  const [status, setStatus] = useState<DisparoMessageStatus | "all">("all");
  const [search, setSearch] = useState("");

  const { messages, total, loading } = useDisparoCampaignMessages(workspaceId, campaignId, {
    status,
    search: search.trim() || undefined,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar telefone ou nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as DisparoMessageStatus | "all")}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {(Object.keys(DISPARO_MESSAGE_STATUS_LABELS) as DisparoMessageStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {DISPARO_MESSAGE_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{total} mensagem(ns)</span>
      </div>

      {loading ? (
        <div className="space-y-2 py-1">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Nenhuma mensagem encontrada.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Destinatário</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Enviada em</TableHead>
              <TableHead>Erro</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.map((msg) => (
              <TableRow key={msg.id}>
                <TableCell>{msg.lead_name || "—"}</TableCell>
                <TableCell className="font-mono text-sm">
                  {formatPhoneForDisplay(msg.phone)}
                </TableCell>
                <TableCell>
                  <Badge variant={msg.status === "failed" ? "destructive" : "secondary"}>
                    {DISPARO_MESSAGE_STATUS_LABELS[msg.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {msg.sent_at
                    ? format(new Date(msg.sent_at), "dd/MM/yy HH:mm", { locale: ptBR })
                    : "—"}
                </TableCell>
                <TableCell
                  className="text-sm text-destructive max-w-[260px] whitespace-normal"
                  title={msg.error ?? undefined}
                >
                  {friendlyDisparoError(msg.error, msg.phone)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function friendlyDisparoError(error: string | null | undefined, phone: string): string {
  if (!error) return "—";
  const e = error.toLowerCase();
  const digits = (phone || "").replace(/\D/g, "");
  if (e.includes("group members") || digits.length > 13) {
    return "Telefone inválido no cadastro (parece ter mais de um número no mesmo campo)";
  }
  if (e.includes("not on whatsapp")) return "Este número não tem WhatsApp";
  if (e.includes("timed out") || e.includes("timeout")) return "O WhatsApp não respondeu a tempo";
  if (e.includes("463")) return "Envio bloqueado temporariamente pelo WhatsApp";
  if (e.includes("disconnect") || e.includes("not connected")) return "Conexão desconectada no momento do envio";
  return error;
}
